import { useState } from "react";
import { isReauthRequired } from "@/hooks/use-binding";
import { useDestinationTest } from "@/hooks/use-destination-readiness";
import { useAuth } from "@/lib/auth/AuthContext";
import { isMfaRequired } from "@/lib/auth/mfaStepUp";
import { useInlineError } from "@/lib/feedback/InlineErrorBar";
import type { DestinationTestView } from "@shared/types/instancesAdmin";

/** The test's re-authentication could not confirm identity. */
const TEST_REAUTH_FAILED =
  "We could not confirm your identity. Try again, or sign out and back in if this keeps happening.";

type TestMode = "test" | "check";

/** A Test or Check now held while the person confirms their identity again. */
export interface TestReauth {
  mode: TestMode;
  code: "REAUTH_REQUIRED" | "MFA_REQUIRED";
  error: string | null;
}

/**
 * #923: the last Test or Check now pressed on the destination page, shown until the next save,
 * with the re-authentication hold the test shares with Save (FR-14): a refusal opens the same
 * dialog and, once identity is confirmed, runs the same test again.
 */
export function useDestinationTestRun() {
  const tester = useDestinationTest();
  const [testResult, setTestResult] = useState<DestinationTestView | null>(null);
  const testError = useInlineError();
  const { reauthenticate } = useAuth();
  const [testReauth, setTestReauth] = useState<TestReauth | null>(null);
  const runTest = async (mode: TestMode) => {
    testError.clear();
    try {
      setTestResult(await tester.mutateAsync({ mode }));
    } catch (cause) {
      if (isReauthRequired(cause)) {
        setTestReauth({
          mode,
          code: isMfaRequired(cause) ? "MFA_REQUIRED" : "REAUTH_REQUIRED",
          error: null,
        });
        return;
      }
      testError.show(cause, "The test could not run.");
    }
  };
  const retryTestAfterReauth = async () => {
    if (!testReauth) return;
    const { mode } = testReauth;
    let outcome: Awaited<ReturnType<typeof reauthenticate>>;
    try {
      outcome = await reauthenticate();
    } catch {
      outcome = "failed";
    }
    // A redirecting provider leaves the page; the admin presses the button again on return.
    if (outcome === "redirecting") return;
    if (outcome === "failed") {
      setTestReauth({ ...testReauth, error: TEST_REAUTH_FAILED });
      return;
    }
    setTestReauth(null);
    await runTest(mode);
  };
  return {
    tester,
    testResult,
    setTestResult,
    testError,
    testReauth,
    setTestReauth,
    runTest,
    retryTestAfterReauth,
  };
}
